import { useRouter } from "next/navigation";

import { priceFormat } from "@/shared.utils/number-format";
import { Button } from "@/shared-web.ui/ui.button";
import { Flex } from "@/shared-web.ui/ui.flex";
import { Text } from "@/shared-web.ui/ui.text";
import { cn } from "@/shared-web.ui/util.cn";
import { buildUrl } from "@/shared-web.ui/util.url";
import { getCheckout } from "@/stf.data/request.checkout";

import { useCartContext } from "./cart.context";

interface CartFooterProps {
  className?: string;
}
const CartFooter = (props: CartFooterProps) => {
  const { className } = props;

  const { cartState, dispatchCartAction } = useCartContext();
  const {
    selectedCartItemId,
    isSubmitting,
    totalDetail: {
      itemCount,
      finalPrice,
      stickerPrice,
    },
  } = cartState;
  const { replace } = useRouter();

  const handleCheckout = async () => {
    try {
      dispatchCartAction({
        type: "setIsSubmitting",
        payload: true,
      });
      const checkoutSearchParams = new URLSearchParams();
      checkoutSearchParams.set("timestamp", Date.now().toString());
      checkoutSearchParams.set("selected", selectedCartItemId.join("."));

      // This will cause 2 duplicate call to getCheckout API
      // but it's needed because for better UX,
      // we need to catch error on this cart modal first before redirect
      // we'll add cache to server side to reduce server load
      await getCheckout(
        {
          selectedItems: selectedCartItemId,
        },
      );
      replace(
        buildUrl("/checkout", checkoutSearchParams),
      );
    }
    catch (e) {
      dispatchCartAction({
        type: "setError",
        payload: {
          error: e,
          defaultMsg: "Unknown errors while creating orders.",
        },
      });
      dispatchCartAction({
        type: "setIsSubmitting",
        payload: false,
      });
    }
  };

  return (
    <Flex
      className={cn(
        "w-full bg-accent-2",
        className,
      )}
      p="3"
      direction="column"
    >
      <Flex justify="between">
        <Flex>
          <Text>
            Total
            {itemCount ? ` (${itemCount} items)` : null}
          </Text>
        </Flex>
        <Flex direction="row" gap="2" align="baseline">
          {finalPrice === stickerPrice
            ? (
                <Text align="right" as="span">
                  {priceFormat(finalPrice)}
                </Text>
              )
            : (
                <>
                  <Text
                    align="right"
                    as="span"
                    size="1"
                    color="gray"
                    className="line-through"
                  >
                    {priceFormat(stickerPrice)}
                  </Text>
                  <Text>
                    {priceFormat(finalPrice)}
                  </Text>
                </>
              )}
        </Flex>
      </Flex>
      <Flex>
        <Text size="1">
          VAT included, where applicable
        </Text>
      </Flex>
      <Flex className="my-2 mt-3">
        <Button
          className="w-full"
          disabled={selectedCartItemId.length === 0 || isSubmitting}
          onClick={handleCheckout}
          size="3"
        >
          Check Out
        </Button>
      </Flex>
    </Flex>
  );
};

export { CartFooter };
